import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { JsonLDService } from '../../services/json-ld.service';
import { convertToStoryblokCdn } from '../pipes/storyblok-cdn.pipe';

export const storyblokArticleJsonLDGuard: CanActivateFn = async route => {
  const storyblokService = inject(PICFLOW_STORYBLOK);
  const jsonLDService = inject(JsonLDService);
  const config = inject(PICFLOW_CONFIG);

  const path = getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home';

  const story = await firstValueFrom(
    storyblokService.getStory(path, route).pipe(map(resp => (resp && resp.story) || (false as const)))
  );

  if (!story) return true;

  const image = story.content.metaImage || story.content.image;
  const imageUrl = image?.filename
    ? convertToStoryblokCdn(image.filename, config.storyblokUrl) + '/m/1200x0/filters:format(jpg)'
    : undefined;

  const authorId = story.content.author;
  const relationships = await firstValueFrom(storyblokService.resolveRelationships(route));
  const author = authorId ? relationships.find(r => r.uuid === authorId) : undefined;

  jsonLDService.add('page', {
    '@type': 'Article',
    image: imageUrl,
    url: `${config.correctPicflowUrl}/${story.full_slug}`,
    dateCreated: story.first_published_at,
    datePublished: story.first_published_at,
    dateModified: story.published_at,
    headline: story.content.metaTitle || story.content.title,
    name: story.content.metaTitle || story.content.title,
    description: story.content.metaDescription || story.content.lead,
    identifier: story.id.toString(),
    author: author
      ? {
          '@type': 'Person',
          name: author.content.name,
          url: author.content.url,
        }
      : undefined,
    '@context': 'https://schema.org/',
  });

  return true;
};
